import {
  requirePricingCatalogPrice,
  type StandardPlanCode,
} from "@optrixis/config/pricing";
import { TRIAL_DAYS } from "@optrixis/config/trial";

const MARKETING_PRICING_COUNTRY = "US";

function marketingPricing(planCode: StandardPlanCode) {
  const monthly = requirePricingCatalogPrice({
    planCode,
    countryCode: MARKETING_PRICING_COUNTRY,
    billingInterval: "MONTHLY",
  });

  const annual = requirePricingCatalogPrice({
    planCode,
    countryCode: MARKETING_PRICING_COUNTRY,
    billingInterval: "ANNUAL",
  });

  return {
    monthly: monthly.amountMinor / 100,
    annualMonthly: annual.amountMinor / 100 / 12,
    annualTotal: annual.amountMinor / 100,
  };
}

const START_PRICING = marketingPricing("START");
const GROWTH_PRICING = marketingPricing("GROWTH");
const PRO_PRICING = marketingPricing("PRO");

export const MARKETING_TRIAL_DAYS = TRIAL_DAYS;
export const MARKETING_ANNUAL_SAVINGS_LABEL = "Save ~20%";

export const MARKETING_PLANS = [
  {
    name: "Start",
    monthly: START_PRICING.monthly,
    annualMonthly: START_PRICING.annualMonthly,
    annualTotal: START_PRICING.annualTotal,
    description: "The essentials for one physical business location.",
    previewDescription: "Essential local-growth intelligence for one physical business location.",
    features: [
      "1 physical business location",
      "Up to 2 team members",
      "Google Business Profile connection, subject to provider availability",
      "Review monitoring and AI-assisted reply suggestions",
      "Basic Local Score and Local SEO intelligence",
      "Priority Actions workspace",
      "In-app and email notifications",
      "Monthly reports and 12-month product history",
    ],
    cta: "Request access",
    highlight: false,
  },
  {
    name: "Growth",
    monthly: GROWTH_PRICING.monthly,
    annualMonthly: GROWTH_PRICING.annualMonthly,
    annualTotal: GROWTH_PRICING.annualTotal,
    description: "Advanced intelligence and alerting for a growing local business.",
    previewDescription: "Advanced intelligence and alerting for businesses strengthening their local presence.",
    features: [
      "Everything in Start",
      "Up to 5 team members",
      "Advanced review intelligence",
      "Advanced Local Score and Local SEO intelligence",
      "Critical WhatsApp alerts under policy and consent gates",
      "Weekly insights",
      "24-month product history",
      "Curated additional-channel catalog",
    ],
    cta: "Request access",
    highlight: true,
  },
  {
    name: "Pro",
    monthly: PRO_PRICING.monthly,
    annualMonthly: PRO_PRICING.annualMonthly,
    annualTotal: PRO_PRICING.annualTotal,
    description: "The most complete standard-location operating model.",
    previewDescription: "The deepest standard-plan intelligence and operating context for one location.",
    features: [
      "Everything in Growth",
      "Up to 10 team members",
      "Brand Voice AI context",
      "Critical and high-impact WhatsApp alerts under policy gates",
      "Premium PDF reports",
      "Advanced exports",
      "Full compatible additional-channel catalog",
      "Maximum compliant product-history access",
    ],
    cta: "Request access",
    highlight: false,
  },
  {
    name: "Enterprise",
    monthly: null,
    annualMonthly: null,
    annualTotal: null,
    description: "For organizations managing multiple physical business locations.",
    previewDescription: "Multi-location groups with a contact-led rollout and custom commercial scope.",
    features: [
      "Multi-location management",
      "Portfolio governance",
      "Custom entitlements and rollout",
      "Custom integration planning",
      "Advanced reporting and exports",
      "Policy-aware alerting",
      "Custom commercial terms",
      "Contact-led implementation",
    ],
    cta: "Contact sales",
    highlight: false,
  },
] as const;

export type MarketingPlan = (typeof MARKETING_PLANS)[number];
